// web/src/pages/admin/QuoteTandCsPage.tsx
//
// Q-8 — Admin CRUD for quote T&C templates (`/api/quote-tandcs`).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  quoteTandCsApi,
  type QuoteTandCsTemplateDto,
  type CreateQuoteTandCsTemplateRequest,
  type UpdateQuoteTandCsTemplateRequest,
} from "@/lib/quote-admin";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Drawer form state
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  tandCsText: string;
  isDefault: boolean;
}

function emptyForm(): FormState {
  return { name: "", tandCsText: "", isDefault: false };
}

function dtoToForm(dto: QuoteTandCsTemplateDto): FormState {
  return {
    name: dto.name,
    tandCsText: dto.tandCsText,
    isDefault: dto.isDefault,
  };
}

// ---------------------------------------------------------------------------
// QuoteTandCsPage
// ---------------------------------------------------------------------------

export function QuoteTandCsPage() {
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<QuoteTandCsTemplateDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["quote-tandcs"],
    queryFn: () => quoteTandCsApi.list(),
  });

  const visible = templates.filter((t) => !t.deleted);

  function openNew() {
    setEditing(null);
    setForm(emptyForm());
    setDrawerOpen(true);
  }

  function openEdit(t: QuoteTandCsTemplateDto) {
    setEditing(t);
    setForm(dtoToForm(t));
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
  }

  const save = useMutation({
    mutationFn: () => {
      if (editing) {
        const body: UpdateQuoteTandCsTemplateRequest = {
          name: form.name,
          tandCsText: form.tandCsText,
          isDefault: form.isDefault,
        };
        return quoteTandCsApi.update(editing.quoteTandCsTemplateId, body);
      } else {
        const body: CreateQuoteTandCsTemplateRequest = {
          name: form.name,
          tandCsText: form.tandCsText,
          isDefault: form.isDefault,
        };
        return quoteTandCsApi.create(body);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-tandcs"] });
      toast.success(editing ? "T&C template updated." : "T&C template created.");
      closeDrawer();
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  const del = useMutation({
    mutationFn: (id: number) => quoteTandCsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-tandcs"] });
      toast.success("T&C template deleted.");
    },
    onError: (err: Error) => {
      const msg = err.message.toLowerCase().includes("409") || err.message.toLowerCase().includes("in use")
        ? "T&C template is in use by one or more quotes."
        : err.message;
      toast.error(`Delete failed: ${msg}`);
    },
  });

  const cols: DataGridColumn<QuoteTandCsTemplateDto>[] = [
    { id: "name", header: "Name", accessor: (t) => t.name },
    {
      id: "preview",
      header: "T&Cs preview",
      accessor: (t) =>
        t.tandCsText.length > 80
          ? `${t.tandCsText.slice(0, 80)}…`
          : t.tandCsText,
    },
    {
      id: "default",
      header: "Default",
      accessor: (t) => (t.isDefault ? "Yes" : "No"),
    },
    {
      id: "created",
      header: "Created",
      accessor: (t) =>
        t.createdOn ? new Date(t.createdOn).toLocaleDateString("en-AU") : "—",
    },
    {
      id: "actions",
      header: "",
      accessor: (t) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => del.mutate(t.quoteTandCsTemplateId)}
            disabled={del.isPending}
          >
            Delete
          </Button>
        </div>
      ),
      align: "right",
    },
  ];

  return (
    <>
      <ListPage
        title="Quote T&C Templates"
        primaryAction={
          <IntentButton onClick={openNew} aria-label="New T&C template">
            + New T&C Template
          </IntentButton>
        }
      >
        <DataGrid
          rows={visible}
          columns={cols}
          rowKey={(t) => t.quoteTandCsTemplateId}
          totalRows={visible.length}
          pageIndex={0}
          pageSize={visible.length || 1}
          loading={isLoading}
          emptyHeadline="No T&C templates found."
          onPageChange={() => void 0}
        />
      </ListPage>

      <DetailsDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={editing ? "Edit T&C Template" : "New T&C Template"}
      >
        <div className="space-y-4 p-4">
          <div>
            <Label htmlFor="tandcs-name">Name *</Label>
            <Input
              id="tandcs-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Template name"
            />
          </div>
          <div>
            <Label htmlFor="tandcs-text">T&Cs text *</Label>
            <textarea
              id="tandcs-text"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              rows={12}
              value={form.tandCsText}
              onChange={(e) => setForm((f) => ({ ...f, tandCsText: e.target.value }))}
              placeholder="Enter terms and conditions text…"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="tandcs-default"
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
            />
            <Label htmlFor="tandcs-default">Set as default T&C template</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || !form.name || !form.tandCsText}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={closeDrawer}>
              Cancel
            </Button>
          </div>
        </div>
      </DetailsDrawer>
    </>
  );
}
